import TablePagination from "../TablePagination";
import { ITEMS_PER_PAGE } from "../../api/common/pagination";
import { useState } from "react";

type PaginationPlacement = "top" | "bottom" | "both";

export type DashboardTableProps<T> = {
    data: T[],
    headings: string[],
    renderRowCells: (rowData: T) => React.ReactNode,
    itemName?: string,
    pageSize?: number,
    paginationPlacement?: PaginationPlacement
}

const DashboardTable = <T extends { id: string }>({
    data,
    headings,
    renderRowCells,
    itemName = "items",
    pageSize = ITEMS_PER_PAGE,
    paginationPlacement = "bottom",
}: DashboardTableProps<T>) => {
    const [currentPage, setCurrentPage] = useState(0);
    
    // Calculate pagination
    const startIndex = currentPage * pageSize;
    const endIndex = Math.min(startIndex + pageSize, data.length);
    const currentPageData = data.slice(startIndex, endIndex);

    const renderPagination = () => {
        if (data.length <= pageSize) return null;
        
        return (
            <TablePagination
                totalItems={data.length}
                pageSize={pageSize}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                itemName={itemName}
            />
        );
    };

    return (
        <div className="rounded-lg border border-surface overflow-hidden">
            {/* Top Pagination */}
            {(paginationPlacement === "top" || paginationPlacement === "both") && (
                <div className="border-b border-surface">
                    {renderPagination()}
                </div>
            )}

            {/* Table Content */}
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-secondary text-white border-b-[3px] border-b-primary text-[0.875rem] whitespace-nowrap">
                        <tr className="child:px-4 sm:child:px-6 child:py-2 sm:child:py-4">
                            {headings.map(heading => (
                                <th key={heading}>{heading}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {currentPageData.map((rowData, index) => (
                            <tr key={rowData.id} 
                                className={`
                                    child:px-4 sm:child:px-6 child:py-2 sm:child:py-4 
                                    ${index < currentPageData.length - 1 ? "border-b border-surface" : ""} 
                                    text-[0.875rem] break-words
                                `}
                            >
                                {renderRowCells(rowData)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Bottom Pagination */}
            {(paginationPlacement === "bottom" || paginationPlacement === "both") && renderPagination()}
        </div>
    );
};

export default DashboardTable;